/**
 * KLineChartContainer - Main Chart Component (JSX Version)
 * Migrated from TradingView to KLineChart
 * Renders planetary lines and trend lines for financial astrology
 * 
 * @version 2.3.0 - Fixed dual-model (geo/helio) trendline support
 */

import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useSelector } from "react-redux";
import { Button } from "react-bootstrap";
import { init, dispose, registerIndicator } from 'klinecharts';
import { cloneDeep, isEmpty, debounce } from 'lodash';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';

// RTK Query hooks - same as TradingView component
import { useLazyGetPlanetAspectsQuery, useLazyGetPlanetPositionsQuery } from 'store/services/astrology.service';
import { useLazyGetGannWheelAspectsQuery } from 'store/services/workbook.chart.service';
import Loader from "components/shared/Loader";

// Redux actions
import { updateByName } from 'store/workbook/workbookSlice';

// Utilities
import {
  calcAspectDegree,
  calculatePlanetLinesData,
  calculateTrendLineProjection,
  convertLongitudeToPrice,
  reversePlanetLine,
  isPointWithinTimeAndPriceRange,
  displayTooltip,
  removeTooltip,
  formatString,
  mergeDegrees,
  calculateFutureDate,
  // createNewPlanet,
  isTimestampMilliseconds,
  mergeTimestamps,
  sortTimestamps,
  timeStampRange,
} from './utils/functions';

// Constants - FIXED: Added TRENDLINE_COLORS import
import {
  PLANET_COLORS,
  DEFAULT_CHART_STYLES,
  DEFAULT_CONFIG,
  OVERLAY_NAMES,
  PREDICTION_LINE_CONFIG,
  MODEL_TYPES,
  TRENDLINE_COLORS,
} from './constants';

// Overlays
import { registerAllOverlays } from './overlays';

import logo from "../../../../../Assets/images/logo.png";

// Styles
// import './KLineChartContainer.css';

// Register overlays on module load
registerAllOverlays();

export function registerWatermarkIndicator(options = {}) {
  const {
    // Text options
    text = 'YOUR BRAND',
    fontSize = 80,
    fontFamily = 'Arial, sans-serif',
    color = 'rgba(136, 136, 136, 0.07)',
    fontWeight = 'bold',
    letterSpacing = 8,

    // Image options
    imageSrc = null,          // URL or imported image path (e.g. '/logo.png' or imported asset)
    imageWidth = 200,         // Desired display width
    imageHeight = 200,        // Desired display height
    imageOpacity = 0.06,      // Grey opacity like Binance (0.04 - 0.08 works well)
    imageOnly = false,        // If true, only show image, no text
    imageAboveText = true,    // If false, image renders below text
    gap = 10,                 // Gap between image and text when both are shown
  } = options;

  // Pre-load the image outside the draw loop so it's cached
  let watermarkImage = null;
  let imageLoaded = false;
  let imageLoadFailed = false;

  if (imageSrc) {
    watermarkImage = new Image();
    watermarkImage.crossOrigin = 'anonymous'; // Handle CORS if needed
    watermarkImage.onload = () => {
      imageLoaded = true;
      console.log('Watermark image loaded successfully');
    };
    watermarkImage.onerror = (err) => {
      imageLoadFailed = true;
      console.warn('Watermark image failed to load:', imageSrc, err);
    };
    watermarkImage.src = imageSrc;
  }

  try {
    registerIndicator({
      name: 'Watermark',
      shortName: '',
      series: 'price',
      figures: [],

      calc: (dataList) => dataList.map(() => ({})),

      draw: ({ ctx, bounding }) => {
        if (!ctx || !bounding) return false;

        const { width, height } = bounding;

        ctx.save();

        // --- Calculate total content height for centering ---
        let totalContentHeight = 0;
        const showImage = imageSrc && imageLoaded && !imageLoadFailed;
        const showText = !imageOnly || !showImage; // Show text if imageOnly is false, or image failed

        if (showImage) {
          totalContentHeight += imageHeight;
        }
        if (showImage && showText) {
          totalContentHeight += gap;
        }
        if (showText) {
          totalContentHeight += fontSize;
        }

        let currentY = (height - totalContentHeight) / 2;

        // --- Draw Image ---
        if (showImage && imageAboveText) {
          ctx.globalAlpha = imageOpacity;

          // Optional: apply grayscale filter for colored logos (Binance style)
          // This makes any logo look like a grey watermark
          ctx.filter = 'grayscale(100%)';

          const imgX = (width - imageWidth) / 2;
          ctx.drawImage(watermarkImage, imgX, currentY, imageWidth, imageHeight);

          // Reset filter
          ctx.filter = 'none';
          ctx.globalAlpha = 1.0;

          currentY += imageHeight + gap;
        }

        // --- Draw Text ---
        if (showText) {
          ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
          ctx.fillStyle = color;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';

          // Letter spacing implementation
          const chars = text.split('');
          let totalWidth = 0;
          chars.forEach(char => {
            totalWidth += ctx.measureText(char).width + letterSpacing;
          });
          totalWidth -= letterSpacing;

          let x = (width - totalWidth) / 2;

          chars.forEach((char) => {
            const charWidth = ctx.measureText(char).width;
            ctx.fillText(char, x + charWidth / 2, currentY);
            x += charWidth + letterSpacing;
          });

          currentY += fontSize;
        }

        // --- Draw Image below text (if configured) ---
        if (showImage && !imageAboveText) {
          currentY += gap;
          ctx.globalAlpha = imageOpacity;
          ctx.filter = 'grayscale(100%)';

          const imgX = (width - imageWidth) / 2;
          ctx.drawImage(watermarkImage, imgX, currentY, imageWidth, imageHeight);

          ctx.filter = 'none';
          ctx.globalAlpha = 1.0;
        }

        ctx.restore();
        return true;
      }
    });

    console.log('Watermark indicator registered successfully (with image support)');
  } catch (e) {
    console.log('Watermark indicator already registered');
  }
}




export const KLineChartContainer = ({
  defaultProps,
  toolName,
  graphData,
  planets,
  calendar,
  maxDatePast,
  maxDateFuture,
  workbook,
  trendLineConfig,
  helioCentrictrendLineConfig,
  loading,
  setLoading,
  priceUnit = 1,
  chartPriceUnit,
  useChartPriceUnit,
  gmtOffset,
  location,
  timeDegrees: externalTimeDegrees,
  trendLineData: externalTrendLineData,
  ...props
}) => {
  const { highestPriceChange, highestHelioCentricPriceChange, } = workbook || {};
  const { trendlineTimeRange } = useSelector((state) => state.workbook.chartConfig);

  // ============================================
  // Redux
  // ============================================
  const dispatch = useDispatch();

  // ============================================
  // RTK Query Hooks (same as TradingView)
  // ============================================
  const [triggerPlanetPositionsQuery] = useLazyGetPlanetPositionsQuery();
  const [triggerPlanetAspectsQuery] = useLazyGetPlanetAspectsQuery();
  const [triggerTrendLineDataQuery] = useLazyGetGannWheelAspectsQuery();

  // ============================================
  // Refs
  // ============================================
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const chartDegreesRef = useRef({});
  const overlayIdsRef = useRef([]);
  const priceRangeOverlayIdRef = useRef(null);
  
  // Refs for tracking trendline enabled state (prevents stale closure issues)
  const geoTrendlineEnabledRef = useRef(false);
  const helioTrendlineEnabledRef = useRef(false);

  const chartUpdateToastId = "updateChartRange";

  // ============================================
  // State
  // ============================================
  const [conversionFactor, setConversionFactor] = useState(priceUnit);
  // Initialize with external timeDegrees if provided
  const [timeDegrees, setTimeDegrees] = useState(externalTimeDegrees || {});
  const [planetAspects, setPlanetAspects] = useState({});
  const [graph, setGraph] = useState([]);
  const [trendLineData, setTrendLineData] = useState(null);
  const [chartPriceRange, setChartPriceRange] = useState({ from: 0, to: 0 });
  const [chartTimeRange, setChartTimeRange] = useState({ from: 0, to: 0 });
  const [graphOrigTimeRange, setGraphOrigTimeRange] = useState({ start: null, end: null });
  const [loadedTimestamps, setLoadedTimestamps] = useState([]);
  const [isActivePlanetLine, setIsActivePlanetLine] = useState(true);
  const [isActiveTrendLine, setIsActiveTrendLine] = useState(false);
  const [isActivePriceRangeTool, setIsActivePriceRangeTool] = useState(false);
  const [isChartReady, setIsChartReady] = useState(false);

  // Geo and Helio trendline states
  const [geoTrendlineEnabled, setGeoTrendlineEnabled] = useState(false);
  const [geoTrendLineData, setGeoTrendLineData] = useState(null);
  const [helioTrendlineEnabled, setHelioTrendlineEnabled] = useState(false);
  const [helioTrendLineData, setHelioTrendLineData] = useState(null);

  // Keep refs in sync with state (prevents stale closure issues)
  useEffect(() => {
    geoTrendlineEnabledRef.current = geoTrendlineEnabled;
  }, [geoTrendlineEnabled]);
  
  useEffect(() => {
    helioTrendlineEnabledRef.current = helioTrendlineEnabled;
  }, [helioTrendlineEnabled]);

  // ============================================
  // Computed Data
  // ============================================
  // Calculate planet lines data
  const planetLinesData = useMemo(() => {
    const result = calculatePlanetLinesData(planets, timeDegrees, calendar, graph, graphOrigTimeRange);
    console.log('planetLinesData calculated:', {
      result: result ? Object.keys(result) : null,
      timeDegreesKeys: Object.keys(timeDegrees),
      planetsCount: planets?.length,
      graphLength: graph?.length,
      graphOrigTimeRange
    });
    return result;
  }, [planets, timeDegrees, calendar, graph, graphOrigTimeRange]);

  // Geocentric trend line projection
  const geoTrendLineProjectionData = useMemo(
    () =>
      calculateTrendLineProjection(
        geoTrendLineData,
        graphData,
        chartTimeRange,
        trendLineConfig?.startingPosition,
        maxDatePast,
        highestPriceChange,
        MODEL_TYPES.geo,
        trendlineTimeRange
      ),
    [geoTrendLineData, graphData, chartTimeRange, trendLineConfig?.startingPosition, maxDatePast, highestPriceChange, trendlineTimeRange]
  );

  // Heliocentric trend line projection
  const helioTrendLineProjectionData = useMemo(
    () =>
      calculateTrendLineProjection(
        helioTrendLineData,
        graphData,
        chartTimeRange,
        helioCentrictrendLineConfig?.startingPosition,
        maxDatePast,
        highestHelioCentricPriceChange,
        MODEL_TYPES.helio,
        trendlineTimeRange
      ),
    [helioTrendLineData, graphData, chartTimeRange, helioCentrictrendLineConfig?.startingPosition, maxDatePast, highestHelioCentricPriceChange, trendlineTimeRange]
  );

  // Convert graph data to KLineChart format
  const klineData = useMemo(() => {
    if (!graph?.length) return [];

    const sorted = [...graph].sort((a, b) => 
      moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
    );

    const firstTime = moment(sorted[0].timestamp).utc().startOf('day');
    const lastTime = moment(sorted[sorted.length - 1].timestamp).utc().startOf('day');

    // Build a lookup map for fast matching
    const graphMap = {};
    sorted.forEach(bar => {
      const key = moment(bar.timestamp).utc().startOf('day').valueOf();
      graphMap[key] = bar;
    });

    // Iterate every calendar day, same as TradingView
    const bars = [];
    let current = firstTime.clone();

    while (current.isSameOrBefore(lastTime, 'day')) {
      const key = current.valueOf();
      const bar = graphMap[key];

      if (bar) {
        bars.push({
          timestamp: key,
          open: bar.open,
          high: bar.high,
          low: bar.low,
          close: bar.close,
          volume: bar.volume || 0,
        });
      } else {
        // Zero-price bar for non-trading days
        bars.push({
          timestamp: key,
          open: 0,
          high: 0,
          low: 0,
          close: 0,
          volume: 0,
        });
      }

      current.add(1, 'day');
    }

    return bars;
  }, [graph]);

  // ============================================
  // API Fetch Functions (same as TradingView)
  // ============================================

  /**
   * Fetch planet positions for given timestamps
   */
  const fetchTimeDegrees = async (timestamps, _gmtOffset = "+00:00", _location) => {
    const normalizedTimestamps = timestamps?.map(timestamp =>
      !isTimestampMilliseconds(timestamp) ? timestamp * 1000 : timestamp
    );

    const response = await triggerPlanetPositionsQuery({
      ephemerisDate: normalizedTimestamps,
      gmtOffset: _gmtOffset,
      location: _location,
    }).unwrap();

    return response;
  };

  /**
   * Fetch planet aspects for given timestamps
   */
  const fetchPlanetAspects = async (timestamps) => {
    if (!timestamps?.length) {
      return;
    }
    try {
      const response = await triggerPlanetAspectsQuery({
        ephemerisDate: timestamps,
        gmtOffset,
        location,
      }).unwrap();

      return response;
    } catch (err) {
      console.log("Error fetching planet aspects", err);
    }
  };

  /**
   * Get trend line data from server - FIXED: Proper async function with model parameter
   */
  const getTrendLineData = useCallback(async (model) => {
    try {
      if (!trendlineTimeRange && (!graphData?.length || !maxDatePast || !maxDateFuture)) {
        throw new Error("Graph data not available");
      }

      setLoading(true);

      let timestamps = [];
      let from, to;

      if (trendlineTimeRange) {
        // Use trendlineTimeRange when available
        from = moment(trendlineTimeRange?.startDateTime).unix();
        to = moment(trendlineTimeRange?.endDateTime).unix();
        timestamps = timeStampRange(from, to);
      } else if (calendar?.activeDate) {
        // Use calendar active date with 1 year forward
        from = moment(calendar?.activeDate).unix();
        to = moment(calendar?.activeDate).add(365, "days").unix();
        timestamps = timeStampRange(from, to);
      } else {
        // Default: use graph data range
        const graphLastDay = graphData[graphData.length - 1];
        from = moment(maxDatePast).utc().unix();
        to = calculateFutureDate(graphLastDay.timestamp, maxDateFuture);
        to = moment(to).utc().unix();
        timestamps = timeStampRange(from, to);
      }

      if (!timestamps?.length) {
        throw new Error("No timestamps to fetch");
      }

      const data = await triggerTrendLineDataQuery({
        ephemerisDate: timestamps,
        gmtOffset: gmtOffset,
        stock: workbook?.stock?._id,
        setTimeToStartOfDay: !trendlineTimeRange,
        model,
      }).unwrap();

      if (isEmpty(data)) {
        throw new Error("Trend line data not found");
      }

      // Store data based on model type
      if (model === MODEL_TYPES.helio) {
        setHelioTrendLineData(data);
      } else if (model === MODEL_TYPES.geo) {
        setGeoTrendLineData(data);
      } else {
        throw new Error("Unknown trendline model");
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching trendline data:", err);
      setLoading(false);
      
      // Disable the toggle on error
      if (model === MODEL_TYPES.helio) {
        setHelioTrendlineEnabled(false);
      } else if (model === MODEL_TYPES.geo) {
        setGeoTrendlineEnabled(false);
      }
    }
  }, [graphData, maxDatePast, maxDateFuture, gmtOffset, workbook?.stock?._id, trendlineTimeRange, calendar?.activeDate, triggerTrendLineDataQuery, setLoading]);

  // ============================================
  // Redux/Workbook Updates
  // ============================================

  const handleChangeWorkbook = (name, value) => {
    dispatch(updateByName({ name, value }));
  };

  // ============================================
  // Effects
  // ============================================

  // Calendar view effect (same as TradingView)
  useEffect(() => {
    if (!calendar?.activeDate || !chartRef.current) {
      return;
    }

    try {
      const chart = chartRef.current;
      const fromTimestamp = moment(calendar.activeDate).valueOf();
      chart.scrollToTimestamp(fromTimestamp);
    } catch (error) {
      console.warn("Error setting calendar view:", error);
    }
  }, [calendar?.activeDate]);

  // Conversion Factor Effect
  useEffect(() => {
    if (useChartPriceUnit) {
      setConversionFactor(chartPriceUnit);
    } else {
      setConversionFactor(priceUnit);
    }
  }, [priceUnit, useChartPriceUnit, chartPriceUnit]);

  // Graph Data Effect
  useEffect(() => {
    if (!graphData?.length) {
      return;
    }

    setGraph(graphData);
    setGraphOrigTimeRange({
      start: moment(graphData[0].timestamp).unix(),
      end: moment(graphData[graphData.length - 1].timestamp).unix(),
    });
  }, [graphData]);

  // Sync external timeDegrees prop when provided
  useEffect(() => {
    if (externalTimeDegrees && !isEmpty(externalTimeDegrees)) {
      setTimeDegrees(prev => {
        if (isEmpty(prev)) return externalTimeDegrees;
        // Merge with existing
        const merged = cloneDeep(prev);
        for (const planet in externalTimeDegrees) {
          merged[planet] = {
            ...(merged[planet] || {}),
            ...(externalTimeDegrees[planet] || {})
          };
        }
        return merged;
      });
    }
  }, [externalTimeDegrees]);

  // ============================================
  // Load Planetary Data (same as TradingView)
  // ============================================
  useEffect(() => {
    try {
      if (!graph?.length || !maxDatePast || !maxDateFuture) {
        return;
      }

      const graphFirstTimestamp = graph[0].timestamp;
      const graphLastTimestamp = graph[graph.length - 1].timestamp;

      let fromTimestamp, toTimestamp;

      if (calendar?.activeDate) {
        fromTimestamp = moment(calendar.activeDate).utc().unix();
        toTimestamp = moment(graphFirstTimestamp).utc().unix();
      } else {
        fromTimestamp = moment(maxDatePast).utc().unix();
        toTimestamp = calculateFutureDate(graphLastTimestamp, maxDateFuture);
        toTimestamp = moment(toTimestamp).utc().unix();
      }

      const timestamps = timeStampRange(fromTimestamp, toTimestamp);

      if (!timestamps?.length) {
        return;
      }

      const fetchTimestampsAsync = async () => {
        const newTimeDegrees = await fetchTimeDegrees(timestamps, gmtOffset, location);

        // Merge new degrees with existing planet time degrees
        let timeDegreesClone = cloneDeep(timeDegrees);
        if (isEmpty(timeDegrees)) {
          timeDegreesClone = newTimeDegrees;
        } else {
          for (let planet in timeDegreesClone) {
            let planetDegrees = {
              ...(timeDegreesClone[planet] || {}),
              ...(newTimeDegrees[planet] || {})
            };
            timeDegreesClone[planet] = planetDegrees;
          }
        }

        setTimeDegrees(timeDegreesClone);
        setLoadedTimestamps((prevState) =>
          sortTimestamps(mergeTimestamps(prevState, timestamps))
        );
      };

      const fetchTimestampsPromise = fetchTimestampsAsync();

      toast.promise(fetchTimestampsPromise, {
        loading: 'Updating chart',
        success: 'Chart updated',
        error: 'Failed to update chart',
      }, {
        position: "top-center",
        id: chartUpdateToastId
      });
    } catch (err) {
      console.error("Error handling price range change", err);
    }
  }, [graph, calendar?.activeDate, maxDatePast, maxDateFuture, gmtOffset]);

  // ============================================
  // Calendar Aspects Effect (same as TradingView)
  // ============================================
  useEffect(() => {
    if (!calendar?.activeDate) {
      return;
    }

    async function plotAspects() {
      try {
        setLoading(true);
        const aspectsData = await fetchPlanetAspects([calendar.activeDate]);

        if (!aspectsData) {
          throw new Error("Aspects data not found");
        }

        const aspects = aspectsData[moment(calendar.activeDate).unix()];
        if (isEmpty(aspects)) {
          throw new Error("Aspects data not found for the selected date");
        }

        const newPlanets = [];
        Object.entries(aspects).forEach(([planet, planetData]) => {
          const { isRetrograde, aspects: planetAspects } = planetData;
          if (!planetAspects?.length) {
            return;
          }
          planetAspects.forEach((aspectDetails) => {
            const newPlanet = createNewPlanet({
              name: planet,
              aspect: aspectDetails.aspect,
              orb: aspectDetails.orb,
              direction: isRetrograde ? "reverse" : "forward",
              enabled: false
            });
            if (newPlanet) {
              newPlanets.push(newPlanet);
            }
          });
        });

        handleChangeWorkbook("planets", newPlanets);
        setPlanetAspects(aspects);
        setLoading(false);
      } catch (err) {
        console.error("Failed to plot aspects", err);
        setLoading(false);
      }
    }

    plotAspects();
  }, [calendar]);

  // ============================================
  // Fetch Geo Trendline Data Effect
  // ============================================
  useEffect(() => {
    if (geoTrendLineData || !geoTrendlineEnabled) return;
    getTrendLineData(MODEL_TYPES.geo);
  }, [geoTrendlineEnabled, gmtOffset, geoTrendLineData, calendar?.activeDate, getTrendLineData]);

  // ============================================
  // Fetch Helio Trendline Data Effect
  // ============================================
  useEffect(() => {
    if (helioTrendLineData || !helioTrendlineEnabled) return;
    getTrendLineData(MODEL_TYPES.helio);
  }, [helioTrendlineEnabled, gmtOffset, helioTrendLineData, calendar?.activeDate, getTrendLineData]);

  // ============================================
  // Chart Initialization
  // ============================================
  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Register Y-axis extender indicator (only once)
    try {
      registerIndicator({
        name: 'YAxisExtender',
        shortName: '',
        series: 'price',
        minValue: 0,  
        figures: [],
        calc: (dataList) => dataList.map(() => ({})),
      });
      registerWatermarkIndicator({
        imageSrc: logo,
        imageWidth: 350,
        imageHeight: 250,
        imageOpacity: 0.05,
        imageOnly: true,
      });
    } catch (e) {
      // Already registered, ignore
    }

    const chart = init(chartContainerRef.current, { styles: DEFAULT_CHART_STYLES });
    
    // Add the invisible indicator to force Y-axis to $0 (only if no trendlines active)
    const anyTrendlineActive = geoTrendlineEnabledRef.current || helioTrendlineEnabledRef.current;
    if (!anyTrendlineActive) {
      chart.createIndicator('YAxisExtender', true, { id: 'candle_pane' });
    }
    chart.createIndicator('Watermark', true, { id: 'candle_pane' });
    
    chartRef.current = chart;

    return () => {
      if (chartContainerRef.current) dispose(chartContainerRef.current);
      chartRef.current = null;
      setIsChartReady(false);
    };
  }, []);

  // ============================================
  // Y-Axis Extender Management (when trendlines toggle)
  // ============================================
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const anyTrendlineActive = geoTrendlineEnabled || helioTrendlineEnabled;
    
    try {
      if (anyTrendlineActive) {
        // Remove Y-axis extender to allow negative values
        chart.removeIndicator('candle_pane', 'YAxisExtender');
      } else {
        // Check if YAxisExtender already exists before creating
        const existingIndicators = chart.getIndicators('candle_pane');
        const hasYAxisExtender = existingIndicators?.some(ind => ind.name === 'YAxisExtender');
        
        if (!hasYAxisExtender) {
          // Re-add Y-axis extender to force Y-axis minimum to 0
          chart.createIndicator('YAxisExtender', true, { id: 'candle_pane' });
        }
      }
    } catch (e) {
      // Indicator might not exist or already exists
    }

    // Ensure Watermark is always present
    try {
      const existingIndicators = chart.getIndicators('candle_pane');
      const hasWatermark = existingIndicators?.some(ind => ind.name === 'Watermark');
      
      if (!hasWatermark) {
        chart.createIndicator('Watermark', true, { id: 'candle_pane' });
      }
    } catch (e) {
      // Watermark might already exist
    }
  }, [geoTrendlineEnabled, helioTrendlineEnabled]);

  // ============================================
  // Apply Data to Chart (using setDataLoader for v10)
  // ============================================
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !klineData.length) return;

    // KLineChart v10 uses setDataLoader with callback pattern
    chart.setDataLoader({
      getBars: ({ callback }) => {
        callback(klineData, false); // false = no more data to load
      }
    });

    // Set symbol and period to trigger data load
    chart.setSymbol({ ticker: workbook?.toolName || toolName });
    chart.setPeriod({ span: 1, type: 'day' });

    setIsChartReady(true);

    // Call updateChartRanges multiple times to ensure it captures the price range
    const initTimeout1 = setTimeout(() => updateChartRanges(chart), 100);
    const initTimeout2 = setTimeout(() => updateChartRanges(chart), 500);
    const initTimeout3 = setTimeout(() => updateChartRanges(chart), 1000);
    
    return () => {
      clearTimeout(initTimeout1);
      clearTimeout(initTimeout2);
      clearTimeout(initTimeout3);
    };
  }, [klineData, workbook?.toolName, toolName]);

  // ============================================
  // Chart Range Helper
  // ============================================
  const updateChartRanges = useCallback((chart) => {
    if (!chart) return;

    try {
      const visibleRange = chart.getVisibleRange();
      const dataList = chart.getDataList();
      if (!visibleRange || !dataList.length) return;

      const fromData = dataList[Math.max(0, visibleRange.from)];
      const toData = dataList[Math.min(visibleRange.to, dataList.length - 1)];

      if (fromData && toData) {
        const lastDataTimestamp = dataList[dataList.length - 1].timestamp / 1000;
        const futureBuffer = 365 * 24 * 60 * 60; // 1 year
        setChartTimeRange({
          from: Math.floor(fromData.timestamp / 1000),
          to: lastDataTimestamp + futureBuffer,
        });
      }

      // Try to get price range from pixel conversion first
      const size = chart.getSize('candle_pane', 'main');
      if (size && size.height > 0) {
        const topPrice = chart.convertFromPixel({ y: 0 }, { paneId: 'candle_pane' });
        const bottomPrice = chart.convertFromPixel({ y: size.height }, { paneId: 'candle_pane' });
        if (topPrice?.value !== undefined && bottomPrice?.value !== undefined && 
            topPrice.value !== 0 && bottomPrice.value !== 0) {
          setChartPriceRange({
            from: Math.min(topPrice.value, bottomPrice.value),
            to: Math.max(topPrice.value, bottomPrice.value),
          });
          return;
        }
      }

      // Fallback: Calculate price range from visible data
      const visibleData = dataList.slice(
        Math.max(0, visibleRange.from),
        Math.min(visibleRange.to + 1, dataList.length)
      );
      
      if (visibleData.length > 0) {
        let minPrice = Infinity;
        let maxPrice = -Infinity;
        
        visibleData.forEach(d => {
          if (d.low < minPrice) minPrice = d.low;
          if (d.high > maxPrice) maxPrice = d.high;
        });
        
        // Add some padding (10%)
        const padding = (maxPrice - minPrice) * 0.1;
        
        setChartPriceRange({
          from: minPrice - padding,
          to: maxPrice + padding,
        });
      }
    } catch (error) {
      console.warn('Error updating chart ranges:', error);
    }
  }, []);

  // ============================================
  // Event Subscriptions
  // ============================================
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isChartReady) return;

    const handleRangeChange = debounce(() => updateChartRanges(chart), 200);
    const handleCrosshairChange = debounce((data) => handleTooltip(data), 50);

    chart.subscribeAction('onVisibleRangeChange', handleRangeChange);
    chart.subscribeAction('onCrosshairChange', handleCrosshairChange);
    chart.subscribeAction('onZoom', handleRangeChange);
    chart.subscribeAction('onScroll', handleRangeChange);
    handleRangeChange();

    return () => {
      chart.unsubscribeAction('onVisibleRangeChange', handleRangeChange);
      chart.unsubscribeAction('onCrosshairChange', handleCrosshairChange);
      chart.unsubscribeAction('onZoom', handleRangeChange);
      chart.unsubscribeAction('onScroll', handleRangeChange);
      handleRangeChange.cancel();
      handleCrosshairChange.cancel();
    };
  }, [isChartReady, updateChartRanges]);

  // ============================================
  // Tooltip Finder
  // ============================================
  const findAndShowTooltip = useCallback((timestamp, price, screenX, screenY) => {
    const { TOOLTIP_TIME_THRESHOLD, TOOLTIP_PRICE_THRESHOLD } = DEFAULT_CONFIG;
    let bestMatch = null;
    let minDistance = Infinity;

    for (const planetKey in chartDegreesRef.current) {
      const planetData = chartDegreesRef.current[planetKey];
      if (!planetData?.length) continue;
      for (const point of planetData) {
        const timeDiff = Math.abs(Number(point.time) - timestamp);
        const priceDiff = Math.abs(point.price - price);
        const distance = timeDiff + priceDiff;
        if (timeDiff <= TOOLTIP_TIME_THRESHOLD && priceDiff <= TOOLTIP_PRICE_THRESHOLD && distance < minDistance) {
          minDistance = distance;
          bestMatch = { planetKey, point };
        }
      }
    }

    if (bestMatch) {
      const planetSettings = planets.find((p) => p.key === bestMatch.planetKey);
      if (planetSettings) {
        displayTooltip(`
          <div><strong>Planet:</strong> ${formatString(planetSettings.name)}</div>
          <div><strong>Date:</strong> ${moment.unix(bestMatch.point.time).format('DD/MM/YYYY')}</div>
          <div><strong>Price:</strong> ${bestMatch.point.price.toFixed(2)}</div>
          ${planetSettings.aspect ? `<div><strong>Aspect:</strong> ${formatString(planetSettings.aspect)}</div>` : ''}
        `, screenX, screenY);
      }
    } else {
      removeTooltip();
    }
  }, [planets]);

  // ============================================
  // Tooltip Handler
  // ============================================
  const handleTooltip = useCallback((data) => {
    if (!isActivePlanetLine) { removeTooltip(); return; }
    
    const chart = chartRef.current;
    if (!chart || !data || data.x === undefined || data.y === undefined) { 
      removeTooltip(); 
      return; 
    }
    
    // Convert pixel coordinates to data index and price
    const convertResult = chart.convertFromPixel(
      { x: data.x, y: data.y }, 
      { paneId: 'candle_pane' }
    );
    
    if (!convertResult || convertResult.dataIndex === undefined || convertResult.value === undefined) {
      removeTooltip();
      return;
    }
    
    const dataList = chart.getDataList();
    const currentData = dataList[convertResult.dataIndex];
    
    if (!currentData) { removeTooltip(); return; }
    
    const timestamp = Math.floor(currentData.timestamp / 1000);
    const price = convertResult.value;
    
    // Get screen coordinates for tooltip positioning
    const chartDom = chart.getDom('candle_pane', 'main');
    const rect = chartDom?.getBoundingClientRect();
    const screenX = (rect?.left || 0) + data.x;
    const screenY = (rect?.top || 0) + data.y;
    
    findAndShowTooltip(timestamp, price, screenX, screenY);
  }, [isActivePlanetLine, findAndShowTooltip]);

  // ============================================
  // Remove All Overlays
  // ============================================
  const removeAllOverlays = useCallback(() => {
    const chart = chartRef.current;
    if (!chart) return;
    overlayIdsRef.current.forEach((id) => { try { chart.removeOverlay(id); } catch (e) {} });
    overlayIdsRef.current = [];
  }, []);

  // ============================================
  // Create Single Planet Line Overlay
  // ============================================
  const createSinglePlanetOverlay = useCallback((chart, points, styles) => {
    const { CHART_RANGE_EXTENSION_DAYS, LOD_MAX_LINES } = DEFAULT_CONFIG;
    
    if (chartTimeRange.from === 0 && chartTimeRange.to === 0) {
      console.warn('createSinglePlanetOverlay: Chart time range not set yet');
      return null;
    }
    
    const timeFrom = moment.unix(chartTimeRange.from).subtract(CHART_RANGE_EXTENSION_DAYS, 'days');
    const timeTo = moment.unix(chartTimeRange.to).add(CHART_RANGE_EXTENSION_DAYS, 'days');

    // Only filter by TIME, not by PRICE
    const visiblePoints = points.filter((p) => {
      const pointTime = moment.unix(p.time);
      return pointTime.isBetween(timeFrom, timeTo, null, '[]');
    });

    if (visiblePoints.length < 2) {
      console.log('createSinglePlanetOverlay: Not enough visible points:', visiblePoints.length);
      return null;
    }

    // Level of detail sampling
    const DAY_IN_SECONDS = 86400;
    const visibleDays = (chartTimeRange.to - chartTimeRange.from) / DAY_IN_SECONDS;
    const interval = Math.max(1, Math.ceil(visibleDays / LOD_MAX_LINES));
    const sampledPoints = visiblePoints.filter((_, i) => i % interval === 0);
    
    if (sampledPoints.length < 2) {
      return null;
    }

    // Convert to overlay points format
    const overlayPoints = sampledPoints.map((p) => ({ 
      timestamp: p.time * 1000, 
      value: p.price 
    }));

    // DEBUG: Log price range of this overlay
    const prices = overlayPoints.map(p => p.value);

    try {
      const overlayId = chart.createOverlay({
        name: OVERLAY_NAMES.PLANET_LINE,
        points: overlayPoints,
        extendData: {
          lineColor: styles.lineColor,
          lineWidth: styles.lineWidth,
          lineStyle: styles.lineStyle,
          smooth: true,
          interpolationPoints: DEFAULT_CONFIG.INTERPOLATION_POINTS || 8,
          tension: DEFAULT_CONFIG.BEZIER_TENSION || 0.4,
        },
        lock: true,
        visible: true,
      });
      return overlayId;
    } catch (error) {
      console.error('Error creating overlay:', error);
      return null;
    }
  }, [chartTimeRange]);

  // ============================================
  // Create Planet Line Overlays
  // ============================================
  const createPlanetLineOverlays = useCallback((chart, planetName, planetKey, degrees, planetData) => {
    const createdIds = [];
    const MAX_LINE_ITERATIONS = DEFAULT_CONFIG.MAX_LINE_ITERATIONS || 20;
    
    const visibleMinimum = chartPriceRange.from;
    const visibleMaximum = chartPriceRange.to;
    
    // Use extended range - from $0 to above visible
    const minimum = 0;
    const maximum = visibleMaximum * 1.5;
    
    if (visibleMinimum === 0 && visibleMaximum === 0) {
      console.warn('createPlanetLineOverlays: Chart price range not set');
      return createdIds;
    }

    console.log(`\n========== ${planetName} ==========`);
    console.log(`Visible range: $${visibleMinimum.toFixed(0)} - $${visibleMaximum.toFixed(0)}`);
    console.log(`Target range: $${minimum} - $${maximum.toFixed(0)}`);

    let lineWidth = parseFloat(String(planetData.lineWidth)) || 1;
    if (planetData.orb) lineWidth += parseFloat(String(planetData.orb));
    const lineColor = planetData.lineColor || PLANET_COLORS[planetName] || PLANET_COLORS[planetName.toLowerCase()] || '#1677FF';
    const lineStyle = parseInt(String(planetData.lineStyle)) || 0;

    // Step 1: Convert raw degrees to base prices
    const basePricePoints = Object.entries(degrees)
      .map(([time, degree]) => ({
        time: Number(time),
        price: Number((parseFloat(String(degree)) * conversionFactor).toFixed(2))
      }))
      .filter((p) => p.price > 0)
      .sort((a, b) => a.time - b.time);

    if (!basePricePoints.length) {
      console.warn('No base price points for', planetName);
      return createdIds;
    }

    const baseMinPrice = Math.min(...basePricePoints.map(p => p.price));
    const baseMaxPrice = Math.max(...basePricePoints.map(p => p.price));
    
    console.log(`Base prices: $${baseMinPrice.toFixed(0)} - $${baseMaxPrice.toFixed(0)}`);

    const pricePerCycle = 360 * conversionFactor;
    console.log(`Price per cycle: $${pricePerCycle}`);

    // Store for tooltip
    chartDegreesRef.current[planetKey] = mergeDegrees(chartDegreesRef.current[planetKey] || [], basePricePoints);

    // Draw base line
    console.log('Drawing BASE line...');
    const baseOverlayId = createSinglePlanetOverlay(chart, basePricePoints, { lineColor, lineWidth, lineStyle });
    if (baseOverlayId) {
      createdIds.push(baseOverlayId);
      console.log('✅ Base overlay created');
    } else {
      console.log('❌ Base overlay FAILED');
    }

    // Draw UPWARD iterations
    let currentOffset = pricePerCycle;
    let iterations = 0;
    
    console.log('Drawing UPWARD iterations...');
    while (iterations < MAX_LINE_ITERATIONS) {
      const offsetPoints = basePricePoints.map(point => ({
        time: point.time,
        price: point.price + currentOffset
      }));
      
      const minOffsetPrice = Math.min(...offsetPoints.map(p => p.price));
      
      if (minOffsetPrice > maximum) {
        console.log(`Upward stopped at iteration ${iterations}: min $${minOffsetPrice.toFixed(0)} > max $${maximum.toFixed(0)}`);
        break;
      }
      
      chartDegreesRef.current[planetKey] = mergeDegrees(chartDegreesRef.current[planetKey] || [], offsetPoints);
      const overlayId = createSinglePlanetOverlay(chart, offsetPoints, { lineColor, lineWidth, lineStyle });
      if (overlayId) createdIds.push(overlayId);
      
      currentOffset += pricePerCycle;
      iterations++;
    }
    console.log(`Upward: ${iterations} iterations`);

    // Draw DOWNWARD iterations
    currentOffset = -pricePerCycle;
    iterations = 0;
    
    console.log('Drawing DOWNWARD iterations...');
    while (iterations < MAX_LINE_ITERATIONS) {
      const offsetPoints = basePricePoints
        .map(point => ({
          time: point.time,
          price: point.price + currentOffset
        }))
        .filter(p => p.price > 0);
      
      if (offsetPoints.length === 0) {
        console.log(`Downward stopped at iteration ${iterations}: no positive prices`);
        break;
      }

      const offsetMin = Math.min(...offsetPoints.map(p => p.price));
      const offsetMax = Math.max(...offsetPoints.map(p => p.price));
      console.log(`Downward ${iterations}: ${offsetPoints.length} points, $${offsetMin.toFixed(0)} - $${offsetMax.toFixed(0)}`);
      
      chartDegreesRef.current[planetKey] = mergeDegrees(chartDegreesRef.current[planetKey] || [], offsetPoints);
      const overlayId = createSinglePlanetOverlay(chart, offsetPoints, { lineColor, lineWidth, lineStyle });
      if (overlayId) {
        createdIds.push(overlayId);
        console.log(`✅ Downward overlay ${iterations} created`);
      } else {
        console.log(`❌ Downward overlay ${iterations} FAILED`);
      }
      
      currentOffset -= pricePerCycle;
      iterations++;
    }
    console.log(`Downward: ${iterations} iterations`);

    console.log(`${planetName}: TOTAL ${createdIds.length} overlays created`);
    console.log(`==========================================\n`);
    
    return createdIds;
  }, [chartPriceRange, conversionFactor, createSinglePlanetOverlay]);

  // ============================================
  // Draw Planet Lines
  // ============================================
  const drawPlanetLines = useCallback(() => {
    const chart = chartRef.current;
    if (!chart || isEmpty(planetLinesData)) {
      console.warn('drawPlanetLines: No chart or planetLinesData');
      return;
    }

    console.log('drawPlanetLines starting with:', {
      planetLinesDataKeys: Object.keys(planetLinesData),
      enabledPlanets: planets?.filter(p => p.enabled).map(p => ({ name: p.name, key: p.key }))
    });

    chartDegreesRef.current = {};
    const newOverlayIds = [];

    planets.forEach((planetData) => {
      if (!planetData.enabled) return;
      const planetKey = planetData.key;
      const planetName = planetData.name;
      const planetLines = planetLinesData[planetKey];

      console.log(`Processing planet: ${planetName} (key: ${planetKey})`, {
        hasLines: !isEmpty(planetLines),
        linesCount: planetLines?.length
      });

      if (isEmpty(planetLines)) { 
        console.warn('Planet lines not found for:', planetName, 'key:', planetKey);
        console.log('Available keys in planetLinesData:', Object.keys(planetLinesData));
        return; 
      }

      chartDegreesRef.current[planetKey] = [];
      planetLines.forEach((degrees, idx) => {
        console.log(`Drawing line set ${idx} for ${planetName}, entries:`, Object.keys(degrees).length);
        
        let degreesToDraw = planetData.direction === 'reverse' ? reversePlanetLine(degrees) : degrees;
        const ids = createPlanetLineOverlays(chart, planetName, planetKey, degreesToDraw, planetData);
        newOverlayIds.push(...ids);
        console.log(`Created ${ids.length} overlays for ${planetName}`);

        if (planetData.direction === 'both') {
          const reverseIds = createPlanetLineOverlays(chart, planetName, planetKey, reversePlanetLine(degrees), planetData);
          newOverlayIds.push(...reverseIds);
        }
      });
    });

    overlayIdsRef.current.push(...newOverlayIds);
    console.log('Total overlays created:', newOverlayIds.length);
  }, [planetLinesData, planets, createPlanetLineOverlays]);

  // ============================================
  // Draw Trend Line - FIXED: Accepts projectionData and model parameters
  // ============================================
  const drawTrendLine = useCallback((projectionData, model) => {
    const chart = chartRef.current;
    if (!chart || isEmpty(projectionData)) {
      return;
    }

    // Get model-specific color
    const LINE_COLOR = model === MODEL_TYPES.helio ? TRENDLINE_COLORS.helio : TRENDLINE_COLORS.geo;
    const LINE_WIDTH = 3;
    const MARKER_RADIUS = 3.6;

    // Get ACTUAL visible range from chart (not the extended chartTimeRange)
    let showMarkers = false;
    try {
      const visibleRange = chart.getVisibleRange();
      const dataList = chart.getDataList();
      
      if (visibleRange && dataList && dataList.length > 0) {
        const fromData = dataList[Math.max(0, visibleRange.from)];
        const toData = dataList[Math.min(visibleRange.to, dataList.length - 1)];
        
        if (fromData && toData) {
          const fromTimestamp = fromData.timestamp / 1000;
          const toTimestamp = toData.timestamp / 1000;
          const visibleDays = (toTimestamp - fromTimestamp) / 86400; // seconds to days
          
          showMarkers = visibleDays <= 100;
        }
      }
    } catch (e) {
      console.warn('[TrendLine] Could not calculate visible range:', e);
    }
    const startPrice = parseFloat(projectionData[0]?.price) || 0;
    // Convert and sort all points
    const allPoints = projectionData
  .map(point => {
    let price = parseFloat(point.price);
    
    // If it's the Super Line, we "squeeze" the distance from the start price
    if (model === MODEL_TYPES.helio) {
      price = startPrice + (price - startPrice) * 0.5; // 0.2 is the "closeness" factor
    }

    return {
      timestamp: parseInt(point.time) * 1000,
      value: price,
    };
  })
  .filter(p => !isNaN(p.timestamp) && !isNaN(p.value))
  .sort((a, b) => a.timestamp - b.timestamp);

    if (allPoints.length < 2) return;

    try {
      // Create the line overlay
      const lineId = chart.createOverlay({
        name: 'predictionLine',
        points: allPoints,
        extendData: {
          lineColor: LINE_COLOR,
          lineWidth: LINE_WIDTH,
          showMarkers: false,
        },
        lock: true,
        visible: true,
      });
      if (lineId) overlayIdsRef.current.push(lineId);

      // Create markers overlay if zoomed in enough
      if (showMarkers) {
        const markersId = chart.createOverlay({
          name: 'predictionMarkers',
          points: allPoints,
          extendData: {
            markerColor: LINE_COLOR,
            markerRadius: MARKER_RADIUS,
          },
          lock: true,
          visible: true,
        });
        if (markersId) overlayIdsRef.current.push(markersId);
      }

      console.log(`[TrendLine ${model}] ✅ Created with ${allPoints.length} points, color: ${LINE_COLOR}`);
    } catch (error) {
      console.error(`[TrendLine ${model}] Error:`, error);
    }
  }, []);

  // ============================================
  // Main Drawing Effect - FIXED: Uses new geo/helio projection data
  // ============================================
  useEffect(() => {
    // Must have chart, graph, conversion factor AND valid price range
    if (!chartRef.current || isEmpty(graph) || !conversionFactor) {
      console.log('Drawing skipped - missing prerequisites:', {
        hasChart: !!chartRef.current,
        graphLength: graph?.length,
        conversionFactor
      });
      return;
    }

    // Skip if price range not yet calculated
    if (chartPriceRange.from === 0 && chartPriceRange.to === 0) {
      console.log('Drawing skipped - price range not ready yet');
      return;
    }
    console.log(conversionFactor,'conversionFactor');
    
    const fetchD = setTimeout(async () => {
      try {
        const chart = chartRef.current;
        if (!chart) return;

        console.log('Drawing effect triggered:', {
          isActivePlanetLine,
          planetLinesDataKeys: planetLinesData ? Object.keys(planetLinesData) : null,
          timeDegreesKeys: Object.keys(timeDegrees),
          chartPriceRange,
          chartTimeRange,
          planetsCount: planets?.length,
          enabledPlanets: planets?.filter(p => p.enabled).map(p => p.name),
          geoTrendlineEnabled,
          helioTrendlineEnabled,
        });

        removeAllOverlays();

        // Draw planet lines
        if (isActivePlanetLine && !isEmpty(planetLinesData)) {
          console.time("DrawLines");
          drawPlanetLines();
          console.timeEnd("DrawLines");
          console.log('Overlays created:', overlayIdsRef.current.length);
        }
        console.log('Current overlay IDs:', overlayIdsRef.current);
        // Draw geocentric trend line
        if (geoTrendlineEnabled && !isEmpty(geoTrendLineProjectionData)) {
          drawTrendLine(geoTrendLineProjectionData, MODEL_TYPES.geo);
        }

        // Draw heliocentric trend line
        if (helioTrendlineEnabled && !isEmpty(helioTrendLineProjectionData)) {
          drawTrendLine(helioTrendLineProjectionData, MODEL_TYPES.helio);
        }
      } catch (error) {
        console.warn("Error drawing shapes:", error);
      }
    }, 500);

    return () => {
      if (fetchD) {
        clearTimeout(fetchD);
      }
    };
  }, [
    timeDegrees, 
    priceUnit, 
    planets, 
    chartPriceRange, 
    graph, 
    chartTimeRange, 
    conversionFactor, 
    isActivePlanetLine, 
    planetLinesData,
    geoTrendlineEnabled,
    geoTrendLineProjectionData,
    helioTrendlineEnabled,
    helioTrendLineProjectionData,
    drawPlanetLines,
    drawTrendLine,
    removeAllOverlays
  ]);

  // ============================================
  // Toggle Handlers
  // ============================================
  const handlePlanetaryLinesToggle = () => {
    setIsActivePlanetLine(!isActivePlanetLine);
  };

  const handleGeoTrendLineToggle = () => setGeoTrendlineEnabled((prev) => !prev);
  const handleHelioTrendLineToggle = () => setHelioTrendlineEnabled((prev) => !prev);

  const handlePriceRangeToolToggle = () => {
    const chart = chartRef.current;
    if (!chart) return;

    const newState = !isActivePriceRangeTool;
    setIsActivePriceRangeTool(newState);

    if (newState) {
      const overlayId = chart.createOverlay({
        name: 'priceRange',
        lock: false,
        mode: 'normal',
        onDrawEnd: () => {
          setIsActivePriceRangeTool(false);
          return true;
        },
        onRightClick: ({ overlay }) => {
          try { chart.removeOverlay(overlay.id); } catch (e) {}
          priceRangeOverlayIdRef.current = null;
          setIsActivePriceRangeTool(false);
          return true;
        },
      });
      priceRangeOverlayIdRef.current = overlayId;
    } else {
      if (priceRangeOverlayIdRef.current) {
        try { chart.removeOverlay(priceRangeOverlayIdRef.current); } catch (e) {}
        priceRangeOverlayIdRef.current = null;
      }
    }
  };

  const resetChartView = () => {
    try {
      if (!chartRef.current) return;
      chartRef.current.scrollToRealTime();
    } catch (error) {
      console.warn("Error resetting chart:", error);
    }
  };

  // ============================================
  // Render
  // ============================================
  return (
    <>
      <div className="tradingChartCTC d-flex align-items-center mb-3 gap-10 mb-4">
        <ul className="date list-unstyled ps-0 mb-0 d-flex align-items-center gap-10">
          <li className="">
            <Button
              className="active d-flex p-2 py-1 border align-items-center justify-content-center"
              variant="transparent"
            >
              1D
            </Button>
          </li>
          <li className="">
            <Button
              className="d-flex p-2 py-1 border align-items-center justify-content-center"
              variant="transparent"
            >
              1M
            </Button>
          </li>
          <li className="">
            <Button
              className="d-flex p-2 py-1 border align-items-center justify-content-center"
              variant="transparent"
            >
              6M
            </Button>
          </li>
          <li className="">
            <Button
              className="d-flex p-2 py-1 border align-items-center justify-content-center"
              variant="transparent"
            >
              1Y
            </Button>
          </li>
          <li className="">
            <Button
              className="d-flex p-2 py-1 border align-items-center justify-content-center"
              variant="transparent"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="20"
                viewBox="0 0 18 20"
                fill="none"
              >
                <path
                  d="M12.6497 2.25524e-06C13.0742 -0.000994979 13.4099 0.32884 13.4109 0.76862L13.4119 1.51824C16.1665 1.73414 17.9862 3.6112 17.9891 6.48975L18 14.9155C18.0039 18.054 16.0322 19.985 12.8718 19.99L5.15189 20C2.0112 20.004 0.0148232 18.027 0.0108739 14.8796L6.6274e-06 6.55272C-0.00393603 3.65517 1.75153 1.78311 4.50618 1.53024L4.50519 0.780614C4.5042 0.340834 4.83002 0.00999951 5.26445 0.00999951C5.69887 0.00900002 6.02469 0.338835 6.02568 0.778615L6.02666 1.47826L11.8914 1.47027L11.8904 0.770619C11.8894 0.330839 12.2152 0.00100402 12.6497 2.25524e-06ZM13.0525 14.1919H13.0426C12.5884 14.2029 12.2241 14.5837 12.234 15.0435C12.235 15.5032 12.6013 15.882 13.0555 15.892C13.5185 15.891 13.8937 15.5102 13.8927 15.0405C13.8927 14.5707 13.5165 14.1919 13.0525 14.1919ZM4.91691 14.1929C4.46274 14.2129 4.1073 14.5937 4.10828 15.0535C4.12902 15.5132 4.5042 15.8731 4.95837 15.8521C5.40366 15.8321 5.75811 15.4513 5.73738 14.9915C5.7275 14.5417 5.3612 14.1919 4.91691 14.1929ZM8.9847 14.1879C8.53053 14.2089 8.17608 14.5887 8.17608 15.0485C8.19681 15.5082 8.57199 15.8671 9.02617 15.8471C9.47046 15.8261 9.8259 15.4463 9.80517 14.9855C9.79529 14.5367 9.429 14.1869 8.9847 14.1879ZM4.91197 10.5947C4.4578 10.6147 4.10335 10.9955 4.10433 11.4553C4.12408 11.915 4.50025 12.2749 4.95442 12.2539C5.39872 12.2339 5.75317 11.8531 5.73244 11.3933C5.72257 10.9435 5.35725 10.5937 4.91197 10.5947ZM8.98075 10.5597C8.52658 10.5797 8.17114 10.9605 8.17212 11.4203C8.19187 11.8801 8.56804 12.2389 9.02222 12.2189C9.46651 12.1979 9.82096 11.8181 9.80122 11.3583C9.79036 10.9085 9.42505 10.5587 8.98075 10.5597ZM13.0485 10.5647C12.5944 10.5747 12.2389 10.9445 12.2399 11.4043V11.4153C12.2498 11.8751 12.625 12.2239 13.0801 12.2139C13.5244 12.2029 13.8789 11.8221 13.869 11.3623C13.8483 10.9225 13.4918 10.5637 13.0485 10.5647ZM11.8934 3.0095L6.02864 3.01749L6.02962 3.82609C6.02962 4.25687 5.70479 4.5967 5.27037 4.5967C4.83595 4.5977 4.50914 4.25887 4.50914 3.82809L4.50815 3.05847C2.58286 3.25138 1.51754 4.38281 1.52049 6.55072L1.52149 6.86157L16.4696 6.84158V6.49175C16.4272 4.34283 15.349 3.21539 13.4138 3.04748L13.4148 3.81709C13.4148 4.24688 13.0801 4.58771 12.6556 4.58771C12.2212 4.58871 11.8944 4.24888 11.8944 3.81909L11.8934 3.0095Z"
                  fill="url(#paint0_linear_340_3860)"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_340_3860"
                    x1="9"
                    y1="0"
                    x2="9"
                    y2="20"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="#3A674A" />
                    <stop offset="1" stopColor="#0C3D1D" />
                  </linearGradient>
                </defs>
              </svg>
            </Button>
          </li>
        </ul>
        <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10 btnList">
          <li className="">
            <Button
              className={`d-flex align-items-center justify-content-center commonBtn bordredBtn ${isActivePlanetLine ? "active" : ""}`}
              onClick={handlePlanetaryLinesToggle}
            >
              Planetary Lines
            </Button>
          </li>
          <li className="">
            <Button
              onClick={handleGeoTrendLineToggle}
              className={`d-flex align-items-center justify-content-center commonBtn bordredBtn ${geoTrendlineEnabled ? "active" : ""}`}
            >
              Prediction Line{" "}
              <span
                style={{
                  height: "15px",
                  width: "15px",
                  backgroundColor: TRENDLINE_COLORS.geo,
                  marginLeft: "10px",
                  borderRadius: "5px"
                }}
              />
            </Button>
          </li>
          <li className="">
            <Button
              onClick={handleHelioTrendLineToggle}
              className={`d-flex align-items-center justify-content-center commonBtn bordredBtn ${helioTrendlineEnabled ? "active" : ""}`}
            >
              Super Prediction Line{" "}
              <span
                style={{
                  height: "15px",
                  width: "15px",
                  backgroundColor: TRENDLINE_COLORS.helio,
                  marginLeft: "10px",
                  borderRadius: "5px"
                }}
              />
            </Button>
          </li>
          <li className="">
            <Button
              className={`d-flex align-items-center justify-content-center commonBtn bordredBtn ${isActivePriceRangeTool ? "active" : ""}`}
              onClick={handlePriceRangeToolToggle}
            >
              Percentage
            </Button>
          </li>
        </ul>
        <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center gap-10">
          <li className="p-0">
            <Button
              className="d-flex align-items-center justify-content-center themeClr border-0 p-0 fw-sbold"
              variant="transparent"
              onClick={resetChartView}
            >
              Original
            </Button>
          </li>
        </ul>
      </div>

      <div style={{ position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#ffffff',
              opacity: "0.9",
              zIndex: 10,
            }}
          >
            <Loader />
          </div>
        )}
        <div
          ref={chartContainerRef}
          className="KLineChartContainer"
          style={{ width: '100%', height: '480px' }}
        />
      </div>
    </>
  );
};

export default KLineChartContainer;